import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { d as defineCustomElement$9 } from './box.js';
import { d as defineCustomElement$8 } from './button.js';
import { d as defineCustomElement$7 } from './divider.js';
import { d as defineCustomElement$6 } from './icon.js';
import { d as defineCustomElement$5 } from './loader.js';
import { d as defineCustomElement$4 } from './status.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const expandableTileCss = ".header-wrapper{align-items:center;background:none;border:none;color:inherit;cursor:pointer;display:inline-flex;font-size:inherit;justify-content:flex-start;margin:0;padding:0;width:100%}.header-wrapper:disabled{cursor:not-allowed}:host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;border:1px solid var(--jw-theme-surface-border-border-color);border-radius:.375rem;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host header{align-items:center;background:var(--jw-theme-surface-tile-bg-color);border-radius:.375rem;display:flex;justify-content:space-between;padding:1.5rem;width:100%}:host .body-container{background-color:var(--jw-theme-surface-tile-bg-color);display:none;flex-direction:column;padding:1.5rem}:host .footer-container{padding-top:1rem}:host h3{color:var(--jw-theme-surface-text-default-color);font-family:Open Sans;font-size:1.125rem;font-weight:400;letter-spacing:-.0008em;line-height:1.5rem;margin:0}:host h4{color:var(--jw-theme-surface-text-default-color);font-family:Open Sans;font-size:1rem;font-weight:600;letter-spacing:-.0008em;line-height:1.5rem;margin:0}:host .property-slots-container{display:flex;gap:1rem}:host .property-slots-chevron-group{align-items:center;display:flex;gap:1rem}:host .property-slot{align-items:center;color:var(--jw-theme-surface-text-description-color);display:flex}:host .tile-content{background-color:var(--jw-theme-surface-tile-bg-color)}:host .content{display:flex;flex-direction:column;gap:1.5rem}:host([expanded]) .body-container{border-bottom-left-radius:.375rem;border-bottom-right-radius:.375rem;display:flex}:host([expanded]) header{border-bottom:1px solid var(--jw-theme-surface-border-border-color);border-bottom-left-radius:0;border-bottom-right-radius:0}:host([nested]) header{background:var(--jw-theme-surface-level1-default-bg-color)}:host([nested]) .body-container{background-color:var(--jw-theme-surface-level1-default-bg-color);border-radius:.375rem}:host([nested]) .body-container,:host([nested]) .footer-container,:host([nested]) .tile-content{background-color:var(--jw-theme-surface-level1-default-bg-color)}";
const WuiExpandableTileStyle0 = expandableTileCss;

const WuiExpandableTile$1 = /*@__PURE__*/ proxyCustomElement(class WuiExpandableTile extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.expandedChange = createEvent(this, "expandedChange", 7);
        this.toggleExpanded = () => {
            if (this.disabled) {
                return;
            }
            this.expanded = !this.expanded;
            this.expandedChange.emit(this.expanded);
        };
        this.renderPropertySlot = (propertySlot) => {
            if (propertySlot === null || propertySlot === undefined || propertySlot === '') {
                return null;
            }
            if (typeof propertySlot === 'string') {
                return h("span", { class: "property-slot" }, propertySlot);
            }
            return h("wui-status", { ...propertySlot });
        };
        this.hasFooter = () => {
            return this.elm.querySelector('[slot="footer"]') !== null;
        };
        this.contentBox = undefined;
        this.header = '';
        this.propertySlots = [];
        this.nested = false;
        this.expanded = false;
        this.disabled = false;
    }
    render() {
        const slotsWithDividers = [];
        const defaultBoxProps = {
            padding: 0,
        };
        const boxProps = typeof this.contentBox === 'object' ? this.contentBox : {};
        this.propertySlots.forEach((propertySlot, index) => {
            slotsWithDividers.push(this.renderPropertySlot(propertySlot));
            if (index !== this.propertySlots.length - 1) {
                slotsWithDividers.push(h("wui-divider", { type: "vertical" }));
            }
        });
        return (h(Host, { key: 'c288723cdba56217bda51b5343c59585d1a81d1e' }, h("button", { key: '50af23d592ac470f4ff2d7f37eb9d0dd5127507e', type: "button", class: "header-wrapper", disabled: this.disabled, onClick: this.toggleExpanded }, h("header", { key: '2e489a9a9bdbfd7b38ae44b4cad52bdeb62913bb' }, this.nested ? h("h4", null, this.header) : h("h3", null, this.header), h("div", { key: 'fa0f23872a35dc9d5b7eedb5fa53105c67349655', class: "property-slots-chevron-group" }, h("div", { key: '0b8865873f3953fd61339957a2ade99aa459f11f', class: "property-slots-container" }, slotsWithDividers), h("wui-button", { key: 'a3fc3aaee0c5b370aa4929bd8b64d5180037ef5b', type: "icon-ghost", icon: this.expanded ? 'chevron-up' : 'chevron-down', tooltip: this.expanded ? 'Collapse' : 'Expand', disabled: this.disabled })))), h("div", { key: '3befbbe6eab22b9d3b58373ccdf995b42fa38972', class: "body-container" }, h("div", { key: '50a9cfdc1a9076b3e4639bbded0968612c08e15b', class: "content" }, h("wui-box", { key: '748a2b2a9e997505875a958f09b47a6831b418a7', style: { flex: '1' }, ...defaultBoxProps, ...(this.expanded ? boxProps : { ...boxProps, padding: 0 }) }, h("slot", { key: 'ea2e037f4f4a898335b2c95f8b564f5dcb75e504' })), this.hasFooter() && h("wui-divider", { key: 'fcc874ee07adf6406ef91535da15c8d52c61e8c5', type: "horizontal" })), this.hasFooter() && (h("div", { key: '14053363d119715d0881ca8721e462a5e5db7047', class: "footer-container" }, h("slot", { key: '1a7c9018b42dc1c6329da3a34f6c6568315a5470', name: "footer" }))))));
    }
    get elm() { return this; }
    static get style() { return WuiExpandableTileStyle0; }
}, [1, "wui-expandable-tile", {
        "contentBox": [4, "content-box"],
        "header": [1],
        "propertySlots": [16],
        "nested": [516],
        "expanded": [1540],
        "disabled": [516]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-expandable-tile", "wui-box", "wui-button", "wui-divider", "wui-icon", "wui-loader", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-expandable-tile":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiExpandableTile$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiExpandableTile = WuiExpandableTile$1;
const defineCustomElement = defineCustomElement$1;

export { WuiExpandableTile, defineCustomElement };

//# sourceMappingURL=wui-expandable-tile.js.map